<script setup lang="ts">
import Cropper from 'cropperjs';
import 'cropperjs/dist/cropper.css';
import { nextTick, onBeforeUnmount, ref, watch } from 'vue';

export interface RatioOption {
  value: string;
  label: string;
  width: number;
  height: number;
}

const props = defineProps<{
  modelValue: boolean;
  image: File | null;
  ratios?: RatioOption[];
  selectedRatio?: string;
  maxOutputSize?: number;
  quality?: number;
  title?: string;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', v: boolean): void;
  (e: 'update:selectedRatio', v: string): void;
  (e: 'crop', file: File | null): void;
}>();
const { t } = useI18n();

const STANDARD_RATIOS: RatioOption[] = [
  { value: '1:1',  label: '1:1',  width: 1,  height: 1 },
  { value: '3:4',  label: '3:4',  width: 3,  height: 4 },
  { value: '4:3',  label: '4:3',  width: 4,  height: 3 },
  { value: '9:16', label: '9:16', width: 9,  height: 16 },
  { value: '16:9', label: '16:9', width: 16, height: 9 },
  { value: '2:3',  label: '2:3',  width: 2,  height: 3 },
  { value: '3:2',  label: '3:2',  width: 3,  height: 2 },
];

const ratioList = computed(() => props.ratios && props.ratios.length ? props.ratios : STANDARD_RATIOS);
const maxOutputSize = computed(() => props.maxOutputSize ?? 2048);
const quality = computed(() => props.quality ?? 0.9);

const imgRef = ref<HTMLImageElement | null>(null);
const imgSrc = ref<string>('');
const isProcessing = ref(false);
const activeRatio = ref<string>('');
let cropper: Cropper | null = null;

function destroyCropper() {
  if (cropper) {
    cropper.destroy();
    cropper = null;
  }
  if (imgSrc.value) {
    URL.revokeObjectURL(imgSrc.value);
    imgSrc.value = '';
  }
}

async function initCropper() {
  destroyCropper();
  if (!props.image) return;
  imgSrc.value = URL.createObjectURL(props.image);
  await nextTick();
  if (!imgRef.value) return;

  cropper = new Cropper(imgRef.value, {
    viewMode: 1,
    dragMode: 'move',
    guides: true,
    cropBoxMovable: true,
    cropBoxResizable: true,
    autoCropArea: 1,
    responsive: true,
    restore: true,
    checkOrientation: true,
    ready() {
      const data = cropper?.getImageData();
      if (!data) return;
      const imageRatio = data.naturalWidth / data.naturalHeight;
      const closest = ratioList.value.reduce((prev, curr) => {
        const prevR = prev.width / prev.height;
        const currR = curr.width / curr.height;
        return Math.abs(currR - imageRatio) < Math.abs(prevR - imageRatio) ? curr : prev;
      });
      const r = closest.width / closest.height;
      cropper?.setAspectRatio(r);
      activeRatio.value = closest.value;
      emit('update:selectedRatio', closest.value);
    },
  });
}

watch(
  () => [props.modelValue, props.image] as const,
  async ([open, img]) => {
    if (open && img) {
      await initCropper();
    } else {
      destroyCropper();
    }
  },
  { immediate: true },
);

watch(
  () => props.selectedRatio,
  (v) => {
    if (v && v !== activeRatio.value) activeRatio.value = v;
  },
);

onBeforeUnmount(destroyCropper);

function pickRatio(r: RatioOption) {
  const aspect = r.width / r.height;
  cropper?.setAspectRatio(aspect);
  activeRatio.value = r.value;
  emit('update:selectedRatio', r.value);
}

async function getCroppedFile(): Promise<File | null> {
  if (!cropper || !props.image) return null;
  const canvas = cropper.getCroppedCanvas({
    maxWidth: maxOutputSize.value,
    maxHeight: maxOutputSize.value,
    imageSmoothingEnabled: true,
    imageSmoothingQuality: 'high',
  });
  if (!canvas) return null;
  return new Promise<File | null>((resolve) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) return resolve(null);
        const baseName = props.image!.name.replace(/\.[^/.]+$/, '');
        resolve(new File([blob], `${baseName}_cropped.jpg`, {
          type: 'image/jpeg',
          lastModified: Date.now(),
        }));
      },
      'image/jpeg',
      quality.value,
    );
  });
}

async function onConfirm() {
  isProcessing.value = true;
  try {
    const f = await getCroppedFile();
    emit('crop', f);
    emit('update:modelValue', false);
  } finally {
    isProcessing.value = false;
  }
}

function onCancel() {
  emit('crop', null);
  emit('update:modelValue', false);
}

function close(v: boolean) {
  if (!v) onCancel();
  else emit('update:modelValue', v);
}
</script>

<template>
  <v-dialog :model-value="modelValue" @update:model-value="close" max-width="720">
    <v-card class="bg-neutral-900 text-white">
      <v-card-title class="text-lg font-semibold pa-4">{{ title ?? t('editor.cropImage') }}</v-card-title>
      <v-card-text class="pa-4">
        <div class="cropper-wrap">
          <img v-if="imgSrc" ref="imgRef" :src="imgSrc" alt="" />
        </div>
        <div class="ratio-row">
          <button
            v-for="r in ratioList" :key="r.value" type="button"
            class="ratio-chip"
            :class="{ active: activeRatio === r.value }"
            @click="pickRatio(r)"
          >
            <span
              class="chip-thumb"
              :style="{
                width: (r.width >= r.height ? 14 : 14 * (r.width / r.height)) + 'px',
                height: (r.height >= r.width ? 14 : 14 * (r.height / r.width)) + 'px',
              }"
            />
            {{ r.label }}
          </button>
        </div>
      </v-card-text>
      <v-card-actions class="pa-4 justify-end gap-2">
        <button class="btn-outline" :disabled="isProcessing" @click="onCancel">{{ t('editor.cancel') }}</button>
        <button class="btn-primary" :disabled="isProcessing" @click="onConfirm">
          {{ isProcessing ? t('editor.processing') : t('editor.confirm') }}
        </button>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped lang="scss">
.cropper-wrap {
  @apply rounded-lg overflow-hidden bg-neutral-800;
  max-height: 50vh;
  img { display: block; max-width: 100%; max-height: 50vh; }
}
.ratio-row { @apply flex flex-wrap justify-center gap-2 mt-4; }
.ratio-chip {
  @apply inline-flex items-center gap-1.5 rounded-full border border-neutral-700 bg-transparent px-3 py-1.5 text-sm text-white/80 transition-colors;
  min-width: 68px;
  &:hover { @apply border-amber-500/60 bg-amber-500/10; }
  &.active { @apply border-amber-500 bg-amber-500 text-black; }
}
.chip-thumb { @apply inline-block border border-current; }
.btn-outline {
  @apply inline-flex items-center justify-center rounded-full border border-neutral-700 px-4 py-2 text-sm hover:bg-neutral-800;
  &:disabled { @apply opacity-60 cursor-not-allowed; }
}
.btn-primary {
  @apply inline-flex items-center justify-center rounded-full px-5 py-2 text-sm font-semibold text-white;
  background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
  &:disabled { @apply opacity-60 cursor-not-allowed; }
}
</style>
